import React, { useState } from 'react';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import adminApi, { pagingParams } from '../../Api/adminApi';
import PaginatedTable from '../../MyComponents/PaginatedTable/PaginatedTable';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import StatTile from './StatTile';
import DailyBars from './DailyBars';
import { breakdownColumns, pageColumns, topEventColumns, userColumns } from './eventsColumns';
import { fmtNumber, toDateParam } from './adminFormat';

function defaultFrom() {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
}

const BREAKDOWNS = [
    { dimension: 'platform', title: 'Platform' },
    { dimension: 'lang', title: 'Language' },
    { dimension: 'screen', title: 'Screen' },
    { dimension: 'authType', title: 'Login type' },
];

// One breakdown dimension as a compact three-column list (value, visitors, events).
const BreakdownCard = ({ title, rows }) => {
    const classes = useAdminStyles();
    return (
        <div className={classes.card} style={{ padding: '16px 18px' }}>
            <h3 className={classes.cardTitle} style={{ fontSize: '0.95rem', marginBottom: '10px' }}>
                {title}
            </h3>
            {rows.length === 0 ? (
                <p className={classes.muted} style={{ margin: 0, fontSize: '0.86rem' }}>
                    No data.
                </p>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
                    <thead>
                        <tr>
                            {breakdownColumns.map((col) => (
                                <th
                                    key={col.id}
                                    className={classes.muted}
                                    style={{
                                        textAlign: col.numeric ? 'right' : 'left',
                                        fontWeight: 500,
                                        padding: '0 0 6px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={`${row.value}-${index}`}>
                                {breakdownColumns.map((col) => (
                                    <td
                                        key={col.id}
                                        style={{
                                            textAlign: col.numeric ? 'right' : 'left',
                                            padding: '4px 0',
                                            paddingLeft: col.numeric ? '12px' : 0,
                                            borderTop: '1px solid #EEF3F3',
                                            overflowWrap: 'anywhere',
                                        }}
                                    >
                                        {col.render ? col.render(row[col.id], row) : row[col.id]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

/**
 * First-party product analytics read from BigQuery: daily page views and
 * visitors, the most frequent events (server paged and sorted), the most viewed
 * pages and breakdowns by platform, language, screen and login type. The server
 * answers 200 { configured: false } when BIGQUERY_DATASET is not set.
 */
const EventsTab = ({ onOpenUser }) => {
    const classes = useAdminStyles();
    const [from, setFrom] = useState(defaultFrom);
    const [to, setTo] = useState(new Date());
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('count');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [pagesPage, setPagesPage] = useState(0);
    const [pagesRowsPerPage, setPagesRowsPerPage] = useState(10);
    const [usersOrder, setUsersOrder] = useState('desc');
    const [usersOrderBy, setUsersOrderBy] = useState('events');
    const [usersPage, setUsersPage] = useState(0);
    const [usersRowsPerPage, setUsersRowsPerPage] = useState(25);

    const dateQuery = {
        from: from && !isNaN(from.getTime()) ? toDateParam(from) : '',
        to: to && !isNaN(to.getTime()) ? toDateParam(to) : '',
    };
    const dateKey = JSON.stringify(dateQuery);
    const topQuery = { ...pagingParams({ rowsPerPage, page, orderBy, order }), ...dateQuery };
    const usersQuery = {
        ...pagingParams({ rowsPerPage: usersRowsPerPage, page: usersPage, orderBy: usersOrderBy, order: usersOrder }),
        ...dateQuery,
    };

    const summary = useAdminQuery(() => adminApi.getEventsSummary(dateQuery), [dateKey]);
    const configured = summary.data?.configured !== false;
    const top = useAdminQuery(() => adminApi.getEventsTop(topQuery), [JSON.stringify(topQuery)], configured);
    const pages = useAdminQuery(() => adminApi.getEventsPages(dateQuery), [dateKey], configured);
    const breakdowns = useAdminQuery(() => adminApi.getEventsBreakdowns(dateQuery), [dateKey], configured);
    const users = useAdminQuery(() => adminApi.getEventsUsers(usersQuery), [JSON.stringify(usersQuery)], configured);

    const resetPaging = () => {
        setPage(0);
        setPagesPage(0);
        setUsersPage(0);
    };

    const setOrderFlags = (nextOrder, nextOrderBy) => {
        setOrder(nextOrder);
        setOrderBy(nextOrderBy);
        setPage(0);
    };

    if (summary.isForbidden || (summary.isError && !summary.data)) {
        return <StateCard isError={summary.isError} isForbidden={summary.isForbidden} />;
    }

    if (summary.data?.configured === false) {
        return (
            <div className={`${classes.stateCard} ${classes.stateCardWarn}`}>
                BigQuery analytics is not configured on this server. Set BIGQUERY_DATASET (see GCP/ANALYTICS.md).
            </div>
        );
    }

    const totals = summary.data?.totals || {};
    const daily = summary.data?.daily || [];
    // Key rows by the whole triplet: the same action appears under several categories/labels.
    const topRows = (top.data?.rows || []).map((row) => ({
        ...row,
        id: `${row.category}|${row.action}|${row.label}`,
    }));
    const pageRows = pages.data?.rows || [];
    const breakdownRows = breakdowns.data?.rows || [];
    const userRows = users.data?.rows || [];
    const pageSlice = pageRows.slice(pagesPage * pagesRowsPerPage, (pagesPage + 1) * pagesRowsPerPage);
    const sectionError = (q) => (q.isError ? <StateCard isError /> : null);

    return (
        <>
            <p className={classes.muted} style={{ margin: '0 0 14px', fontSize: '0.9rem' }}>
                First-party events only (GA4 is separate). Data is cached for 10 minutes.
            </p>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <div className={classes.filters}>
                    <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        inputVariant="outlined"
                        size="small"
                        format="dd/MM/yyyy"
                        label="From"
                        value={from}
                        onChange={(d) => {
                            setFrom(d);
                            resetPaging();
                        }}
                        KeyboardButtonProps={{ 'aria-label': 'change from date' }}
                        className={classes.filterControl}
                    />
                    <KeyboardDatePicker
                        disableToolbar
                        variant="inline"
                        inputVariant="outlined"
                        size="small"
                        format="dd/MM/yyyy"
                        label="To"
                        value={to}
                        onChange={(d) => {
                            setTo(d);
                            resetPaging();
                        }}
                        KeyboardButtonProps={{ 'aria-label': 'change to date' }}
                        className={classes.filterControl}
                    />
                </div>
            </MuiPickersUtilsProvider>

            {summary.isLoading && !summary.data ? (
                <StateCard isLoading />
            ) : (
                <>
                    <section className={classes.section}>
                        <span className={classes.kicker}>Totals</span>
                        <div className={classes.tileGrid}>
                            <StatTile label="Page views" value={fmtNumber(totals.pageViews)} />
                            <StatTile label="Visitors" value={fmtNumber(totals.visitors)} hint="Distinct browsers" />
                            <StatTile label="Sessions" value={fmtNumber(totals.sessions)} />
                            <StatTile label="Events" value={fmtNumber(totals.events)} />
                            <StatTile
                                label="Signed-in users"
                                value={fmtNumber(totals.users)}
                                hint="Distinct accounts seen"
                            />
                        </div>
                    </section>

                    <section className={classes.section}>
                        <span className={classes.kicker}>Per day</span>
                        <div className={classes.card}>
                            <DailyBars daily={daily} />
                        </div>
                    </section>
                </>
            )}

            <section className={classes.section}>
                <span className={classes.kicker}>Users</span>
                {sectionError(users) || (
                    <PaginatedTable
                        title={`Signed-in users (${fmtNumber(users.data?.totalCount || 0)})`}
                        headCells={userColumns}
                        rows={userRows}
                        rowCount={users.data?.totalCount || 0}
                        rowKey="userId"
                        order={usersOrder}
                        orderBy={usersOrderBy}
                        setOrder={(nextOrder, nextOrderBy) => {
                            setUsersOrder(nextOrder);
                            setUsersOrderBy(nextOrderBy);
                            setUsersPage(0);
                        }}
                        page={usersPage}
                        setPage={setUsersPage}
                        rowsPerPage={usersRowsPerPage}
                        setRowsPerPage={(n) => {
                            setUsersRowsPerPage(n);
                            setUsersPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        isLoading={users.isLoading}
                        onRowClick={onOpenUser ? (row) => onOpenUser(row.userId) : undefined}
                        emptyMessage="No signed-in activity in this period."
                    />
                )}
            </section>

            <section className={classes.section}>
                <span className={classes.kicker}>Top events</span>
                {sectionError(top) || (
                    <PaginatedTable
                        title={`Top events (${fmtNumber(top.data?.totalCount || 0)})`}
                        headCells={topEventColumns}
                        rows={topRows}
                        rowCount={top.data?.totalCount || 0}
                        rowKey="id"
                        order={order}
                        orderBy={orderBy}
                        setOrder={setOrderFlags}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={(n) => {
                            setRowsPerPage(n);
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        isLoading={top.isLoading}
                        emptyMessage="No events in this period."
                    />
                )}
            </section>

            <section className={classes.section}>
                <span className={classes.kicker}>Pages</span>
                {sectionError(pages) || (
                    <PaginatedTable
                        title={`Pages (${fmtNumber(pageRows.length)})`}
                        headCells={pageColumns}
                        rows={pageSlice}
                        rowCount={pageRows.length}
                        rowKey="page"
                        order="desc"
                        orderBy="views"
                        setOrder={() => {}}
                        page={pagesPage}
                        setPage={setPagesPage}
                        rowsPerPage={pagesRowsPerPage}
                        setRowsPerPage={(n) => {
                            setPagesRowsPerPage(n);
                            setPagesPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        isLoading={pages.isLoading}
                        emptyMessage="No page views in this period."
                    />
                )}
            </section>

            <section className={classes.section}>
                <span className={classes.kicker}>Breakdowns</span>
                {sectionError(breakdowns) ||
                    (breakdowns.isLoading && !breakdowns.data ? (
                        <StateCard isLoading />
                    ) : (
                        <div
                            className={classes.tileGrid}
                            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))' }}
                        >
                            {BREAKDOWNS.map((b) => (
                                <BreakdownCard
                                    key={b.dimension}
                                    title={b.title}
                                    rows={breakdownRows.filter((row) => row.dimension === b.dimension)}
                                />
                            ))}
                        </div>
                    ))}
            </section>
        </>
    );
};

export default EventsTab;
